import { useLazyQuery } from "@apollo/client/react";
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    timelineItemClasses,
    TimelineSeparator,
} from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'motion/react';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { varFade } from 'src/components/animate';
import Iconify from 'src/components/Iconify';
import { FaxStatusLabel } from 'src/components/labels/StatusLabels';
import DialogTitle from 'src/dialogs/DialogTitle';
import { GetClientFaxHistoryQuery } from 'src/graphql/fax-queries';
import { GET_FAX_DOCUMENT_DOWNLOAD_URL } from 'src/graphql/resolvers/queries';
import { FaxJobHistoryStatus, FaxJobHistoryType } from '../../../../../../enums/shared.enums';

// Extract types from generated GraphQL types
type FaxJob = NonNullable<GetClientFaxHistoryQuery['clientFaxHistory']>[0];
type JobHistoryEntry = NonNullable<FaxJob['jobHistory']>[0];

interface Props {
    faxRecord: FaxJob;
    onClose: () => void;
}

interface PhaseStatusProps {
    historyEntry: JobHistoryEntry;
}

const getPhaseLabel = (type: FaxJobHistoryType): string => {
    switch (type) {
        case FaxJobHistoryType.WAITING_FOR_WORKER:
            return 'Waiting for Worker';
        case FaxJobHistoryType.GENERATING_PDFS:
            return 'Generating PDFs';
        case FaxJobHistoryType.FAXING_DOCUMENTS:
            return 'Faxing Documents';
        default:
            return type;
    }
};

const getStatusIcon = (status: FaxJobHistoryStatus) => {
    switch (status) {
        case FaxJobHistoryStatus.QUEUED:
            return { icon: 'eva:clock-outline', color: 'warning.main' };
        case FaxJobHistoryStatus.IN_PROGRESS:
            return { icon: 'eva:loader-outline', color: 'info.main' };
        case FaxJobHistoryStatus.COMPLETE:
            return { icon: 'eva:checkmark-circle-2-outline', color: 'success.main' };
        case FaxJobHistoryStatus.CANCELLED:
            return { icon: 'eva:close-circle-outline', color: 'text.disabled' };
        case FaxJobHistoryStatus.FAILED:
            return { icon: 'eva:alert-triangle-outline', color: 'error.main' };
        default:
            return { icon: 'eva:question-mark-circle-outline', color: 'text.secondary' };
    }
};

const PhaseStatus = ({ historyEntry }: PhaseStatusProps) => {
    const { type, status, startedAt, completedAt, metadata, errorMessage } = historyEntry;
    const { icon, color } = getStatusIcon(status);
    const isFailed = status === FaxJobHistoryStatus.FAILED;
    const hasMetadata = metadata && Object.entries(metadata).length > 0;

    return (
        <TimelineItem>
            <TimelineSeparator>
                <TimelineDot
                    sx={{
                        bgcolor: color,
                        boxShadow: 'none',
                        p: 1,
                        m: 0.5,
                    }}
                >
                    <Iconify icon={icon} width={16} height={16} sx={{ color: 'common.white' }} />
                </TimelineDot>
                <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
                <Box sx={{ ml: 2 }}>
                    <Typography variant="subtitle2" sx={{ color, mb: 0.5 }}>
                        {getPhaseLabel(type)}
                    </Typography>
                    <Stack spacing={0.5} sx={{ color: 'text.secondary' }}>
                        {startedAt && (
                            <Typography variant="caption">
                                Started {format(new Date(startedAt), 'MMM dd, yyyy HH:mm:ss')}
                            </Typography>
                        )}
                        {completedAt && (
                            <Typography variant="caption">
                                Completed {format(new Date(completedAt), 'MMM dd, yyyy HH:mm:ss')}
                            </Typography>
                        )}

                        <AnimatePresence>
                            {hasMetadata && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                >
                                    {(type === FaxJobHistoryType.GENERATING_PDFS &&
                                        !!metadata?.pageCount) ||
                                    (type === FaxJobHistoryType.FAXING_DOCUMENTS &&
                                        (!!metadata?.transmissionDuration ||
                                            !!metadata?.confirmationId)) ||
                                    isFailed ? (
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                mt: 1,
                                                bgcolor: 'background.default',
                                                border: '1px solid',
                                                borderColor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.12)'
                                                        : 'rgba(0, 0, 0, 0.08)',
                                                borderRadius: 1,
                                                '& .MuiTypography-caption': {
                                                    color: 'text.secondary',
                                                },
                                                '& .MuiAlert-root': {
                                                    bgcolor: 'transparent',
                                                    p: 0,
                                                    mt: 1,
                                                    '& .MuiAlert-message': {
                                                        p: 0,
                                                    },
                                                },
                                            }}
                                        >
                                            <Stack spacing={0.5}>
                                                {type === FaxJobHistoryType.GENERATING_PDFS &&
                                                    !!metadata?.pageCount && (
                                                        <Typography variant="caption">
                                                            Pages: {metadata.pageCount}
                                                        </Typography>
                                                    )}
                                                {type === FaxJobHistoryType.FAXING_DOCUMENTS &&
                                                    !!metadata?.transmissionDuration && (
                                                        <Typography variant="caption">
                                                            Duration:{' '}
                                                            {metadata.transmissionDuration}s
                                                        </Typography>
                                                    )}
                                                {type === FaxJobHistoryType.FAXING_DOCUMENTS &&
                                                    !!metadata?.confirmationId && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontFamily: 'monospace' }}
                                                        >
                                                            Confirmation ID:{' '}
                                                            {metadata.confirmationId}
                                                        </Typography>
                                                    )}
                                                {isFailed &&
                                                    (!!metadata?.errorType || !!errorMessage) && (
                                                        <Alert severity="error" sx={{ mt: 1 }}>
                                                            <Typography
                                                                variant="caption"
                                                                component="div"
                                                            >
                                                                {metadata?.errorType ||
                                                                    'Unknown Error'}
                                                            </Typography>
                                                            {metadata?.errorCode && (
                                                                <Typography
                                                                    variant="caption"
                                                                    component="div"
                                                                >
                                                                    Code: {metadata.errorCode}
                                                                </Typography>
                                                            )}
                                                            {metadata?.failureStep && (
                                                                <Typography
                                                                    variant="caption"
                                                                    component="div"
                                                                >
                                                                    Failed at:{' '}
                                                                    {metadata.failureStep}
                                                                </Typography>
                                                            )}
                                                        </Alert>
                                                    )}
                                            </Stack>
                                        </Paper>
                                    ) : null}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Stack>
                </Box>
            </TimelineContent>
        </TimelineItem>
    );
};

export default function FaxStatusStep({ faxRecord, onClose }: Props) {
    const { enqueueSnackbar } = useSnackbar();
    const formattedFaxNumber = faxRecord.recipientFax.replace(
        /(\d{3})(\d{3})(\d{4})/,
        '($1) $2-$3'
    );
    const completionEntry = faxRecord.jobHistory?.find(
        (h) =>
            h?.type === FaxJobHistoryType.FAXING_DOCUMENTS &&
            h?.status === FaxJobHistoryStatus.COMPLETE
    );
    const [downloadLoading, setDownloadLoading] = useState(false);

    // Setup query for fetching the download URL
    const [getFaxDocumentDownloadUrl] = useLazyQuery(GET_FAX_DOCUMENT_DOWNLOAD_URL, {
        fetchPolicy: 'no-cache',
    });

    const handleDownload = async () => {
        setDownloadLoading(true);

        if (!faxRecord.faxJobId) {
            enqueueSnackbar('Fax job ID not found', { variant: 'error' });
            setDownloadLoading(false);
            return;
        }

        // Check if S3 details exist
        if (!faxRecord.s3Key || !faxRecord.s3Bucket) {
            enqueueSnackbar('Fax document not found in storage', { variant: 'error' });
            setDownloadLoading(false);
            return;
        }

        try {
            // Execute the query to get a presigned download URL
            const { data, error: downloadError } = await getFaxDocumentDownloadUrl({
                variables: {
                    faxJobId: faxRecord.faxJobId,
                },
            });

            if (downloadError) {
                throw new Error(downloadError.message);
            }

            if (data?.getFaxDocumentDownloadUrl) {
                window.open(data.getFaxDocumentDownloadUrl, '_blank');
                setDownloadLoading(false);
            } else {
                // Handle case where the URL might be null due to server configuration issues
                enqueueSnackbar(
                    'The system is unable to generate a download link. Please contact your administrator to check AWS credentials.',
                    { variant: 'warning' }
                );
                setDownloadLoading(false);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : '';
            // Provide a more user-friendly error message based on the error
            if (message.includes('credential') || message.includes('AWS')) {
                enqueueSnackbar(
                    'AWS configuration issue: The download service is not properly configured. Please contact your administrator.',
                    { variant: 'warning' }
                );
            } else if (message.includes('Cannot query field')) {
                enqueueSnackbar(
                    'Download feature is not available yet. Please contact the administrator to restart the server.',
                    { variant: 'warning' }
                );
            } else {
                enqueueSnackbar(`Error: ${message}`, { variant: 'error' });
            }
            setDownloadLoading(false);
        }
    };

    return (
        <>
            <DialogTitle onClose={onClose}>Fax Status</DialogTitle>
            <DialogContent>
                <Box component={motion.div} variants={varFade().in}>
                    <Stack direction="row" spacing={3}>
                        {/* Left Column - Timeline */}
                        <Paper sx={{ p: 3, flex: '2 1 60%' }}>
                            <Typography variant="h6" sx={{ mb: 3 }}>
                                Transmission Timeline
                            </Typography>

                            <Timeline
                                position="right"
                                sx={{
                                    p: 0,
                                    m: 0,
                                    [`& .${timelineItemClasses.root}:before`]: {
                                        flex: 0,
                                        padding: 0,
                                    },
                                }}
                            >
                                {faxRecord.jobHistory?.map(
                                    (historyEntry) =>
                                        historyEntry && (
                                            <PhaseStatus
                                                key={historyEntry.id}
                                                historyEntry={historyEntry}
                                            />
                                        )
                                )}
                            </Timeline>
                        </Paper>

                        {/* Right Column - Documents and Details */}
                        <Stack spacing={3} sx={{ flex: '1 1 40%' }}>
                            {/* Fax Details */}
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 3 }}>
                                    Fax Details
                                </Typography>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Recipient Fax
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontFamily: 'monospace' }}
                                        >
                                            {formattedFaxNumber}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Patient
                                        </Typography>
                                        <Typography variant="body1">
                                            {faxRecord.clientUser
                                                ? `${faxRecord.clientUser.firstName || ''} ${
                                                      faxRecord.clientUser.lastName || ''
                                                  }`.trim() || faxRecord.clientUser.name
                                                : `ID: ${faxRecord.clientUserId}`}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Sender
                                        </Typography>
                                        <Typography variant="body1">
                                            {faxRecord.requestedBy
                                                ? `${faxRecord.requestedBy.firstName || ''} ${
                                                      faxRecord.requestedBy.lastName || ''
                                                  }`.trim() || faxRecord.requestedBy.name
                                                : `ID: ${faxRecord.requestedById}`}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Status
                                        </Typography>
                                        <FaxStatusLabel status={faxRecord.status} />
                                    </Box>
                                    {!!completionEntry &&
                                        !!completionEntry.metadata?.transmissionDuration && (
                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    color="text.secondary"
                                                    gutterBottom
                                                >
                                                    Transmission Duration
                                                </Typography>
                                                <Typography variant="body1">
                                                    {completionEntry.metadata.transmissionDuration}s
                                                </Typography>
                                            </Box>
                                        )}
                                </Stack>
                            </Paper>

                            {/* Documents List */}
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Documents ({faxRecord.documentSnapshot?.length || 0})
                                </Typography>
                                <Stack spacing={1}>
                                    {(faxRecord.documentSnapshot || []).map((doc) => (
                                        <Stack
                                            key={doc.id}
                                            direction="row"
                                            alignItems="center"
                                            spacing={1.5}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1,
                                                bgcolor: 'background.default',
                                                border: '1px solid',
                                                borderColor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.12)'
                                                        : 'rgba(0, 0, 0, 0.08)',
                                                '&:hover': {
                                                    bgcolor: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? 'rgba(255, 255, 255, 0.08)'
                                                            : 'rgba(0, 0, 0, 0.02)',
                                                },
                                            }}
                                        >
                                            <Iconify
                                                icon="ic:outline-insert-drive-file"
                                                width={20}
                                                height={20}
                                                sx={{ color: 'text.secondary', flexShrink: 0 }}
                                            />
                                            <Typography variant="body2">
                                                {doc.title || ''}
                                                {doc.createdAt &&
                                                    ` - ${format(
                                                        new Date(doc.createdAt),
                                                        'MM/dd/yyyy'
                                                    )}`}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Paper>
                        </Stack>
                    </Stack>
                </Box>
            </DialogContent>

            {/* Action Buttons */}
            {faxRecord.status !== FaxJobHistoryStatus.IN_PROGRESS &&
                faxRecord.status !== FaxJobHistoryStatus.QUEUED && (
                    <DialogActions>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                onClick={handleDownload}
                                startIcon={
                                    downloadLoading ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <Iconify icon="eva:download-outline" />
                                    )
                                }
                                disabled={
                                    downloadLoading ||
                                    faxRecord.status !== FaxJobHistoryStatus.COMPLETE ||
                                    !faxRecord.s3Key
                                }
                            >
                                {downloadLoading ? 'Downloading...' : 'Download Fax Document'}
                            </Button>

                            {faxRecord.status === FaxJobHistoryStatus.FAILED && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => console.log('Retrying fax...')}
                                    startIcon={<Iconify icon="eva:refresh-outline" />}
                                >
                                    Retry Fax
                                </Button>
                            )}
                        </Stack>
                    </DialogActions>
                )}
        </>
    );
}
